import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";

const STORAGE_KEY = "wilink-cookie-consent";
// 13 mois (recommandation CNIL pour le renouvellement du consentement)
const EXPIRY_MS = 13 * 30 * 24 * 60 * 60 * 1000;

function readConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!data?.at || (Date.now() - data.at) > EXPIRY_MS) return null;
    return data;
  } catch {
    return null;
  }
}

function writeConsent(choice) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ choice, at: Date.now() }));
  } catch {}
}

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Décale l'apparition pour éviter le flash lors du premier rendu
    const t = setTimeout(() => {
      if (!readConsent()) setVisible(true);
    }, 600);
    return () => clearTimeout(t);
  }, []);

  const accept = () => {
    writeConsent("accepted");
    setVisible(false);
  };

  const reject = () => {
    writeConsent("rejected");
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-4 inset-x-4 sm:bottom-6 sm:left-6 sm:right-auto sm:max-w-md z-50 animate-in slide-in-from-bottom-5 fade-in duration-500"
      role="dialog"
      aria-labelledby="cookie-title"
      data-testid="cookie-consent-banner"
    >
      <div className="bg-card text-foreground border border-border rounded-xl shadow-2xl p-5 backdrop-blur-md">
        <div className="flex items-start gap-3 mb-3">
          <div className="h-9 w-9 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0">
            <Cookie className="h-4 w-4" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 id="cookie-title" className="text-sm font-bold tracking-tight mb-1">
              Cookies & vie privée
            </h2>
            <p className="text-xs text-muted-foreground leading-relaxed">
              Nous utilisons uniquement des cookies <b>strictement nécessaires</b> au fonctionnement du site (session, préférence de thème). Aucun cookie publicitaire ni traceur tiers.
            </p>
          </div>
          <button
            onClick={reject}
            aria-label="Fermer"
            className="text-muted-foreground hover:text-foreground p-1 -m-1 shrink-0"
            data-testid="cookie-close-btn"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex flex-col-reverse sm:flex-row gap-2 mt-4">
          <button
            onClick={reject}
            className="h-10 px-4 rounded-md border border-border text-sm font-medium hover:bg-accent transition-colors"
            data-testid="cookie-reject-btn"
          >
            Refuser
          </button>
          <button
            onClick={accept}
            className="h-10 px-5 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors flex-1"
            data-testid="cookie-accept-btn"
          >
            Accepter
          </button>
        </div>

        <p className="text-[10px] text-muted-foreground mt-3 leading-relaxed">
          En savoir plus dans notre{" "}
          <Link to="/confidentialite" className="underline hover:text-foreground">
            politique de confidentialité
          </Link>
          .
        </p>
      </div>
    </div>
  );
}
